import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight, Loader2 } from "lucide-react";
import { Plans, errText } from "../../lib/plans";

const ORDER = [
  { tier: "hobbyist", name: "Hobbyist", price: 15, credits: 200 },
  { tier: "creator", name: "Creator", price: 29, credits: 600 },
  { tier: "pro", name: "Pro Suite", price: 59, credits: 1500 },
];

// Upgrade now (prorated charge + prorated extra credits) · downgrade at next renewal.
export function PlanChanger({ plan, onChange }) {
  const [busy, setBusy] = useState(null);
  const cur = ORDER.find((o) => o.tier === plan.tier);
  const change = async (t) => {
    setBusy(t.tier);
    try {
      const r = await Plans.change(t.tier);
      onChange(r);
      if (plan.status === "trialing") toast.success(`Trial switched to ${t.name}. Still no charge until it converts`);
      else if (t.price > cur.price) toast.success(`Upgraded to ${t.name} · +${r.last_upgrade_credits ?? 0} prorated credits added now`);
      else if (t.tier === plan.tier) toast.success("Scheduled downgrade cancelled");
      else toast.success(`${t.name} starts at your next renewal`);
    } catch (e) {
      toast.error(errText(e, "Could not change plan"));
    } finally { setBusy(null); }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-950/60 backdrop-blur-xl p-6" data-testid="plan-changer">
      <p className="font-mono text-[11px] tracking-[0.25em] text-cyan-400">CHANGE PLAN</p>
      <p className="mt-1 text-xs text-slate-400">Upgrades start right away: you pay the prorated difference and get the extra credits for the rest of this month. Downgrades start at your next renewal.</p>
      {plan.pending_tier && (
        <p className="mt-3 text-xs text-amber-300" data-testid="plan-changer-pending">
          Switching to {ORDER.find((o) => o.tier === plan.pending_tier)?.name} at your next renewal.{" "}
          <button className="underline hover:text-white" onClick={() => change(cur)} disabled={!!busy} data-testid="plan-changer-undo">Keep {cur.name}</button>
        </p>
      )}
      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        {ORDER.map((t) => {
          const isCur = t.tier === plan.tier;
          const up = t.price > cur.price;
          const pending = plan.pending_tier === t.tier;
          return (
            <div key={t.tier} className={`rounded-xl border p-4 ${isCur ? "border-cyan-400 bg-cyan-400/10" : "border-white/10"}`} data-testid={`plan-changer-${t.tier}`}>
              <p className="text-white font-semibold">{t.name}</p>
              <p className="font-mono text-xs text-slate-400">${t.price}/mo · {t.credits.toLocaleString()} credits</p>
              {isCur ? <p className="mt-3 text-xs font-mono text-cyan-300">CURRENT</p> : (
                <button onClick={() => change(t)} disabled={!!busy || pending} data-testid={`plan-changer-${t.tier}-btn`}
                  className={`mt-3 w-full flex items-center justify-center gap-1 rounded-full py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${up ? "text-white bg-gradient-to-r from-cyan-500 via-purple-600 to-emerald-500" : "text-slate-200 border border-white/20 hover:border-white/50"}`}>
                  {busy === t.tier ? <Loader2 size={14} className="animate-spin" /> : up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                  {pending ? "Scheduled" : plan.status === "trialing" ? "Switch now" : up ? "Upgrade now" : "Downgrade at renewal"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
